import Utils from "./utils";
import { LOGS_DESTINATION, LOG_MESSAGES } from "./constants";

/*eslint-disable no-unused-vars*/
class Logger {
  debug(data) {}

  info(data) {}

  warn(data) {}

  error(data) {}

  advancedLog(data) {}
}
/*eslint-enable no-unused-vars*/

const DEFAULT_PREFIX = LOG_MESSAGES.DEFAULT_PREFIX;
const LogLevel = {
  DEBUG: 10,
  INFO: 20,
  WARN: 30,
  ERROR: 40,
  ADVANCED_LOG: 50,
};

class LogManagerImpl {
  constructor() {
    this.updateLoggerConfig();
    this.consoleLoggerWrapper = createConsoleLogger();
  }

  writeToClientLogger(level, logStatement) {
    if (!this.hasClientLogger()) {
      return;
    }
    switch (level) {
      case LogLevel.DEBUG:
        return this._clientLogger.debug(logStatement) || logStatement;
      case LogLevel.INFO:
        return this._clientLogger.info(logStatement) || logStatement;
      case LogLevel.WARN:
        return this._clientLogger.warn(logStatement) || logStatement;
      case LogLevel.ERROR:
        return this._clientLogger.error(logStatement) || logStatement;
      case LogLevel.ADVANCED_LOG:
        if(!this._advancedLogWriter) return '';
        return this._clientLogger[this._advancedLogWriter](logStatement) || logStatement;
    }
  }

  isLevelEnabled(level) {
    return level >= this._level;
  }

  hasClientLogger() {
    return this._clientLogger !== null;
  }

  getLogger(options) {
    var prefix = options.prefix || DEFAULT_PREFIX;
    if (this._logsDestination === LOGS_DESTINATION.DEBUG) {
      return this.consoleLoggerWrapper;
    }
    return new LoggerWrapperImpl(prefix);
  }

  updateLoggerConfig(inputConfig) {
    var config = inputConfig || {};
    this._level = config.level || LogLevel.INFO;
    //enabled advancedLogWriter
    this._advancedLogWriter = "warn";
    if (config.advancedLogWriter) {
      this._advancedLogWriter = config.advancedLogWriter;
    }

    if(config.customizedLogger && typeof config.customizedLogger === "object") {
      this.useClientLogger = true;
    }
    this._clientLogger = config.logger || this.selectLogger(config);

    this._logsDestination = LOGS_DESTINATION.NULL;
    if (config.debug) {
      this._logsDestination = LOGS_DESTINATION.DEBUG;
    }
    if (config.logger) {
      this._logsDestination = LOGS_DESTINATION.CLIENT_LOGGER;
    }
  }

  selectLogger(config) {
    if(config.customizedLogger && typeof config.customizedLogger === "object") {
      return config.customizedLogger;
    }
    if(config.useDefaultLogger) {
      this.consoleLoggerWrapper = createConsoleLogger();
      return this.consoleLoggerWrapper;
    }
    return null;
  }
}

class LoggerWrapper {
  debug() {}

  info() {}

  warn() {}

  error() {}

  advancedLog() {}
}

class LoggerWrapperImpl extends LoggerWrapper {
  constructor(prefix) {
    super();
    this.prefix = prefix || DEFAULT_PREFIX;
  }

  debug(...args) {
    return this._log(LogLevel.DEBUG, args);
  }

  info(...args) {
    return this._log(LogLevel.INFO, args);
  }

  warn(...args) {
    return this._log(LogLevel.WARN, args);
  }

  error(...args) {
    return this._log(LogLevel.ERROR, args);
  }

  advancedLog(...args) {
    return this._log(LogLevel.ADVANCED_LOG, args);
  }

  _shouldLog(level) {
    return LogManager.hasClientLogger() && LogManager.isLevelEnabled(level);
  }

  _writeToClientLogger(level, logStatement) {
    return LogManager.writeToClientLogger(level, logStatement);
  }

  _log(level, args) {
    if (this._shouldLog(level)) {
      var logStatement = LogManager.useClientLogger ? args : this._convertToSingleStatement(args, level);
      return this._writeToClientLogger(level, logStatement);
    }
  }

  _convertToSingleStatement(args, logLevel) {
    var date = new Date(Date.now()).toISOString();
    var level = this._getLogLevelByValue(logLevel);
    var logStatement = `[${date}][${level}]`;
    if (this.prefix) {
      logStatement += this.prefix + " ";
    }
    if (this.options) {
      this.options.prefix ? logStatement += " " + this.options.prefix + ":" : logStatement += "";
      this.options.logMetaData ? logStatement += " Meta data: " + JSON.stringify(this.options.logMetaData) : logStatement += "";
    }
    for (var index = 0; index < args.length; index++) {
      var arg = args[index];
      logStatement += this._convertToString(arg) + " ";
    }
    return logStatement;
  }

  _getLogLevelByValue(value) {
    switch(value) {
      case 10: return "DEBUG";
      case 20: return "INFO";
      case 30: return "WARN";
      case 40: return "ERROR";
      case 50: return "ADVANCED_LOG";
    }
  }

  _convertToString(arg) {
    try {
      if (!arg) {
        return "";
      }
      if (Utils.isString(arg)) {
        return arg;
      }
      if (Utils.isObject(arg) && Utils.isFunction(arg.toString)) {
        var toStringResult = arg.toString();
        if (toStringResult !== "[object Object]") {
          return toStringResult;
        }
      }
      return JSON.stringify(arg);
    } catch (error) {
      console.error("Error while converting argument to string", arg, error);
      return "";
    }
  }
}

var createConsoleLogger = () => {
  var logger = new LoggerWrapper();
  logger.debug = (...args) => console.debug.apply(window.console, [].concat(args));
  logger.info = (...args) => console.info.apply(window.console, [].concat(args));
  logger.warn = (...args) => console.warn.apply(window.console, [].concat(args));
  logger.error = (...args) => console.error.apply(window.console, [].concat(args));
  return logger;
};

const LogManager = new LogManagerImpl();

export { LogManager, Logger, LogLevel };
